import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle, Clock, Sparkles } from 'lucide-react';
import { Editor } from '@tiptap/react';
import { reportApi, ReportGenerationProgress } from '../services/reportApi';
import { caseApi } from '../services/api';
import { httpClient } from '../services/httpClient';
import { ReportEditor } from '../components/ReportEditor';
import { SaveStatusIndicator } from '../components/SaveStatusIndicator';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { useAutosave } from '../hooks/useAutosave';
import { markdownToHtml } from '../utils/markdownToProseMirror';
import { useRegisterAskContext } from '../hooks/useRegisterAskContext';
import { useStructuredPiiRegistration } from '../hooks/useStructuredPiiRegistration';

// ── Progress overlay for async report generation ──────────────────────

const STAGE_LABELS: Record<string, string> = {
  PREPARING: 'Preparing case data\u2026',
  GENERATING: 'Generating report with AI\u2026',
  PROCESSING: 'Processing and formatting\u2026',
  COMPLETED: 'Complete',
  FAILED: 'Generation failed',
};

function GenerationProgressOverlay({
  reportId,
  title,
  onComplete,
  onFailed,
}: {
  reportId: string;
  title: string;
  onComplete: () => void;
  onFailed: (error: string | null) => void;
}) {
  const [progress, setProgress] = useState<ReportGenerationProgress | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const maxProgressRef = useRef(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeFiredRef = useRef(false);

  // Poll for progress every 3 seconds
  useEffect(() => {
    let cancelled = false;

    const fetchProgress = async () => {
      if (cancelled) return;
      const data = await reportApi.getGenerationProgress(reportId);
      if (cancelled) return;
      if (data) {
        setProgress(data);
        if ((data.complete || data.failed) && !completeFiredRef.current) {
          completeFiredRef.current = true;
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (data.complete) {
            onComplete();
          } else {
            onFailed(data.error);
          }
        }
      }
    };

    fetchProgress();
    pollingRef.current = setInterval(fetchProgress, 3000);

    return () => {
      cancelled = true;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [reportId, onComplete, onFailed]);

  // Elapsed time ticker
  useEffect(() => {
    const tick = () => {
      if (progress?.startedAtMillis) {
        setElapsed(Math.round((Date.now() - progress.startedAtMillis) / 1000));
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [progress?.startedAtMillis]);

  // Progress never regresses
  const rawProgress = progress ? Math.round(progress.progress * 100) : 0;
  if (rawProgress > maxProgressRef.current) {
    maxProgressRef.current = rawProgress;
  }
  const displayProgress = maxProgressRef.current;

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const elapsedStr = minutes > 0
    ? `${minutes}m ${seconds.toString().padStart(2, '0')}s`
    : `${seconds}s`;

  const stageLabel = progress?.stage
    ? STAGE_LABELS[progress.stage] || progress.stage
    : 'Starting\u2026';

  return (
    <div className="p-6">
      <div className="max-w-lg mx-auto mt-24">
        <div className="card shadow-sm p-8 text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-900/30 mb-4">
              <Sparkles className="h-8 w-8 text-primary-600 dark:text-primary-400 animate-pulse" />
            </div>
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100 mb-1">
              Generating Draft
            </h2>
            <p className="text-sm text-medical-500 dark:text-medical-400">{title}</p>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
            <div
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${displayProgress}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-sm text-medical-600 dark:text-medical-400">
            <span>{stageLabel}</span>
            <span>{elapsedStr}</span>
          </div>

          <p className="mt-6 text-xs text-medical-400 dark:text-medical-500">
            You can navigate away — the report will continue generating in the background.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main page component ───────────────────────────────────────────────

export const ReportEditorPage: React.FC = () => {
  const { reportId, caseId: urlCaseId } = useParams<{ reportId: string; caseId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const { saveStatus, onContentChange, setInitialContent, createSnapshot } = useAutosave({
    reportId: reportId!,
  });

  const { data: report, isLoading, error } = useQuery(
    ['report', reportId],
    () => reportApi.getReport(reportId!),
    {
      enabled: !!reportId,
    }
  );

  const isGenerating = report?.status === 'generating';

  // Fetch case data + metadata for structured PII registration
  const { data: caseData } = useQuery(
    ['case', report?.caseId],
    () => caseApi.getCase(report!.caseId),
    { enabled: !!report?.caseId && !isGenerating }
  );

  const { data: metadata } = useQuery(
    ['metadata', report?.caseId],
    async () => {
      try {
        return await httpClient.get<any>(`/intelligence/metadata/${report!.caseId}`);
      } catch { return null; }
    },
    { enabled: !!report?.caseId && !isGenerating }
  );

  useStructuredPiiRegistration(caseData || undefined, metadata?.patients);

  // Register context with the unified Ask sidebar
  useRegisterAskContext({
    pageType: 'report-editor',
    caseId: report?.caseId,
    reportId: reportId || undefined,
    editor: editorInstance,
    editorSelectedText: selectedText,
  });

  // Set initial content once report loads
  React.useEffect(() => {
    if (report?.content && !isGenerating) {
      const contentStr = typeof report.content === 'string' ? report.content : JSON.stringify(report.content);
      setInitialContent(contentStr);
    }
  }, [report, setInitialContent, isGenerating]);

  const handleContentUpdate = useCallback((newContent: string) => {
    onContentChange(newContent);
  }, [onContentChange]);

  const handleEditorReady = useCallback((editor: Editor) => {
    setEditorInstance(editor);
  }, []);

  const handleSelectionChange = useCallback((text: string | null) => {
    setSelectedText(text);
  }, []);

  const handleRevert = useCallback((content: string) => {
    if (!editorInstance) return;
    try {
      const parsed = JSON.parse(content);
      editorInstance.commands.setContent(parsed, { emitUpdate: false });
      onContentChange(content);
    } catch {
      const html = markdownToHtml(content);
      editorInstance.commands.setContent(html, { emitUpdate: false });
      onContentChange(JSON.stringify(editorInstance.getJSON()));
    }
  }, [editorInstance, onContentChange]);

  const handleGenerationComplete = useCallback(() => {
    // Refetch the report to get the generated content
    queryClient.invalidateQueries(['report', reportId]);
    setGenerationError(null);
  }, [queryClient, reportId]);

  const handleGenerationFailed = useCallback((errorMsg: string | null) => {
    setGenerationError(errorMsg || 'Report generation failed. Please try again.');
    // Refetch to get the failed status
    queryClient.invalidateQueries(['report', reportId]);
  }, [queryClient, reportId]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading report...</span>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Draft not found</h3>
            <p className="text-medical-600 dark:text-medical-400 mb-4">
              The draft you're trying to edit doesn't exist or you don't have access to it.
            </p>
            <Link to="/drafts" className="btn-primary">
              Back to Drafts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show progress overlay while report is being generated
  if (isGenerating) {
    return (
      <GenerationProgressOverlay
        reportId={reportId!}
        title={report.title}
        onComplete={handleGenerationComplete}
        onFailed={handleGenerationFailed}
      />
    );
  }

  // Show error state if generation failed
  if (report.status === 'failed' || generationError) {
    return (
      <div className="p-6">
        <div className="max-w-lg mx-auto mt-24">
          <div className="card shadow-sm p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100 mb-2">
              Draft Generation Failed
            </h2>
            <p className="text-sm text-medical-600 dark:text-medical-400 mb-6">
              {generationError || 'An error occurred while generating this draft.'}
            </p>
            <button
              onClick={() => navigate(urlCaseId ? `/cases/${urlCaseId}/overview` : '/drafts')}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const initialContent = typeof report.content === 'string' ? report.content : JSON.stringify(report.content);

  return (
    <div className="p-6 overflow-hidden max-w-full">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(urlCaseId ? `/cases/${urlCaseId}/drafts/${reportId}` : `/drafts/${reportId}`)}
          className="flex items-center gap-2 text-medical-600 dark:text-medical-400 hover:text-medical-900 dark:hover:text-medical-200 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Draft
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-medical-900 dark:text-medical-100 mb-2">Edit Draft</h1>
            <p className="text-medical-600 dark:text-medical-400">{report.title}</p>
          </div>

          <div className="flex items-center gap-3">
            <SaveStatusIndicator status={saveStatus} />
            <button
              onClick={() => setShowVersionHistory(prev => !prev)}
              className={`p-2 rounded-lg border transition-colors ${
                showVersionHistory
                  ? 'bg-purple-50 border-purple-300 text-purple-700'
                  : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
              title="Version history"
            >
              <Clock className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Editor — now full width, sidebar is in Layout */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="card shadow-sm overflow-hidden">
          {!initialContent ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-primary-600 mr-2" />
              <span className="text-medical-600 dark:text-medical-400">Loading report content...</span>
            </div>
          ) : (
            <ReportEditor
              content={initialContent}
              readOnly={false}
              onUpdate={handleContentUpdate}
              reportId={reportId}
              caseId={report.caseId}
              onEditorReady={handleEditorReady}
              onSelectionChange={handleSelectionChange}
            />
          )}
        </div>
      </div>

      {/* Version History Panel */}
      {showVersionHistory && reportId && (
        <VersionHistoryPanel
          reportId={reportId}
          onClose={() => setShowVersionHistory(false)}
          onRevert={handleRevert}
        />
      )}
    </div>
  );
};
